<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';

import { providePortalTarget } from '@ohmybible/ui';

/**
 * The app's screen. Up to 600px wide (a phone, a narrow window) it is the
 * window itself and this shell adds nothing. Wider, the app is drawn as on a
 * phone, in a frame in the middle of the window (`frame:` in the kit's
 * theme): its content zoomed by the window's height / 900, so a 900px-high
 * window shows it 1 : 1, and clipped to the frame's rounded corners.
 *
 * The stage inside the frame is the containing block of everything `fixed`
 * (the reader, the tab bar, the toasts) and the kit's overlays mount in its
 * layer, so sheets and dialogs open inside the frame. The page scrolls in
 * the stage, not the window. `--frame-height` is the frame's height in the
 * stage's own (zoomed) pixels: the kit's screen-height utilities read it,
 * since a viewport unit inside the zoom would measure the window.
 */
defineSlots<{
  default?: () => unknown;
}>();

const router = useRouter();

/** The window height at which the frame's content is drawn 1 : 1. */
const BASE_HEIGHT = 900;
/** The kit's `frame:` variant, in the same words. */
const FRAMED_QUERY = '(width >= 601px)';

const frame = useTemplateRef<HTMLElement>('frame');
const scroller = useTemplateRef<HTMLElement>('scroller');
const layer = useTemplateRef<HTMLElement>('layer');

const media = window.matchMedia(FRAMED_QUERY);
const framed = ref(media.matches);
const stageStyle = ref<Record<string, string | number>>();

// The router keeps the window's scroll per history entry, and the window does
// not scroll in the frame: the shell does the same for its own scroller — a
// new page starts at its top, Back and Forward return to where it was.
const scrolls = new Map<number, number>();
const historyEntry = () => (window.history.state as { position?: number } | null)?.position;
/** The history entry of the page on screen. */
let shownEntry = historyEntry();
let popped = false;
let enteringByPop = false;

// The frame's box changes with every change of the window that matters (its
// height, or crossing 600px), so its observer drives the zoom; `resize` and
// the media query only make sure nothing is missed.
const measure = () => {
  framed.value = media.matches;
  if (!framed.value || !frame.value) {
    stageStyle.value = undefined;

    return;
  }
  const zoom = window.innerHeight / BASE_HEIGHT;
  stageStyle.value = { 'zoom': zoom, '--frame-height': `${frame.value.clientHeight / zoom}px` };
};

/** Called once the new page is in the DOM (App's page transition, `enter`). */
const restoreScroll = () => {
  shownEntry = historyEntry();
  if (!scroller.value) return;
  scroller.value.scrollTop = enteringByPop && shownEntry !== undefined ? (scrolls.get(shownEntry) ?? 0) : 0;
};

const observer = new ResizeObserver(measure);
// Before a navigation the page it leaves is still on screen (on Back, the
// history entry has already changed: the page's own entry is `shownEntry`).
// Back and Forward: the router's history reports them before it navigates
// (a `popstate` listener of our own would run after the navigation).
const removeHistoryListener = router.options.history.listen((_to, _from, info) => {
  if (info.type === 'pop') popped = true;
});
const removeBeforeEach = router.beforeEach(() => {
  if (shownEntry !== undefined && scroller.value) scrolls.set(shownEntry, scroller.value.scrollTop);
});
// A navigation that renders no new page (pages are keyed by path) stays on
// the page it was on, under a new history entry.
const removeAfterEach = router.afterEach((to, from, failure) => {
  enteringByPop = popped;
  popped = false;
  if (!failure && to.path === from.path) shownEntry = historyEntry();
});

onMounted(() => {
  measure();
  if (frame.value) observer.observe(frame.value);
  window.addEventListener('resize', measure);
  media.addEventListener('change', measure);
});

onBeforeUnmount(() => {
  observer.disconnect();
  removeHistoryListener();
  removeBeforeEach();
  removeAfterEach();
  window.removeEventListener('resize', measure);
  media.removeEventListener('change', measure);
});

// Popovers keep inside the frame while there is one; on a phone, the window.
providePortalTarget(layer, computed(() => (framed.value ? frame.value : null)));

defineExpose({ restoreScroll });
</script>

<template>
  <div class="frame:flex frame:h-dvh frame:items-center frame:justify-center frame:overflow-hidden frame:bg-base">
    <div
      ref="frame"
      class="frame:aspect-frame frame:h-frame frame:max-w-frame frame:overflow-hidden frame:rounded-2xl frame:bg-canvas"
      :data-framed="framed || undefined"
      data-testid="app-frame"
    >
      <div
        class="frame:relative frame:size-full frame:contain-layout frame:contain-paint"
        :style="stageStyle"
        data-testid="app-frame-stage"
      >
        <div
          ref="scroller"
          class="frame:size-full frame:overflow-x-hidden frame:overflow-y-auto"
          data-testid="app-frame-scroller"
        >
          <slot />
        </div>
        <div ref="layer" />
      </div>
    </div>
  </div>
</template>
